import { ComponentRef, useEffect, useRef, type FC, type ReactNode } from 'react'
import { AccessibilityInfo } from 'react-native'

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonSpinner,
  ButtonText,
  Text,
  type AlertDialogProps
} from '@/components'

export interface DialogProps extends Omit<AlertDialogProps, 'children' | 'initiaFocusRef' | 'leastDestructiveRef'> {
  cancelLabel?: string
  children?: ReactNode
  confirmLabel?: string
  confirmTestID?: string
  isDisabled?: boolean
  isLoading?: boolean
  isOpen?: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title?: string
}

export const Dialog: FC<DialogProps> = ({
  cancelLabel = 'Cancel',
  children,
  confirmLabel = 'Confirm',
  confirmTestID,
  initialFocusRef: _initialFocusRef,
  finalFocusRef,
  isDisabled = false,
  isLoading = false,
  isOpen,
  onClose,
  onConfirm,
  size = 'md',
  title = 'Please Confirm',
  ...props
}) => {
  const headerRef = useRef<ComponentRef<typeof AlertDialogHeader>>(null)
  // finalFocusRef restores focus after a CLOSE — but the effect also runs on plain mounts with
  // isOpen false (this dialog stays mounted closed), which would steal AT/keyboard focus to the
  // restore target every time the host screen mounts. Only restore after an actual open.
  const wasOpenRef = useRef(false)
  // while confirming, neither action should be reachable — leaving confirm live lets a second press re-run it
  const isBusy = isDisabled || isLoading

  // instead of passing the finalFocusRef to the gluestack component, we are handling it ourselves. mainly due to the
  // fact that they are using an old AccessibilityInfo method in a stupid way to send focus, and they are also trying
  // to do it immediately in an effect, rather than waiting for the next tick, like we are doing here. this way helps
  // ensure that the component is rendered and mounted before trying to focus it.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    if (isOpen) {
      wasOpenRef.current = true

      const initialFocusRef = _initialFocusRef || headerRef

      timeout = setTimeout(() => {
        const target = initialFocusRef.current

        if (target) {
          target.focus?.()
          AccessibilityInfo.sendAccessibilityEvent?.(target, 'focus')
        }
      }, 0)
    } else if (finalFocusRef && wasOpenRef.current) {
      // Consume the flag so restoration fires exactly once per open/close cycle. Without this a
      // later effect re-run while still closed (e.g. a finalFocusRef identity change) would
      // re-steal focus to the target.
      wasOpenRef.current = false

      timeout = setTimeout(() => {
        const target = finalFocusRef.current

        if (target) {
          target.focus?.()
          AccessibilityInfo.sendAccessibilityEvent?.(target, 'focus')
        }
      }, 0)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [_initialFocusRef, finalFocusRef, headerRef, isOpen])

  return (
    <AlertDialog
      avoidKeyboard
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      {...props}
    >
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader
          accessibilityLabel={`${title} alert dialog`}
          ref={headerRef}
        >
          <Text className='font-poppinsBold text-lg text-white'>{title}</Text>
        </AlertDialogHeader>
        <AlertDialogBody>
          {typeof children === 'string' ? (
            <Text className='text-justify text-sm text-white'>{children ?? 'Are you sure you want to do this?'}</Text>
          ) : (
            children
          )}
        </AlertDialogBody>
        <AlertDialogFooter className='gap-4'>
          <Button
            action='negative'
            className='flex-grow bg-red-500 data-[hover=true]:bg-red-600 data-[active=true]:bg-red-600 data-[disabled=true]:bg-brand-grey-400'
            isDisabled={isBusy}
            onPress={onClose}
            variant='solid'
          >
            <ButtonText className='text-white data-[hover=true]:text-white data-[active=true]:text-white data-[disabled=true]:text-white'>
              {cancelLabel}
            </ButtonText>
          </Button>
          <Button
            accessibilityState={{
              busy: isLoading,
              disabled: isBusy
            }}
            action='positive'
            className='flex-grow bg-green-500 data-[hover=true]:bg-green-600 data-[active=true]:bg-green-600 data-[disabled=true]:bg-brand-grey-400'
            isDisabled={isBusy}
            onPress={onConfirm}
            testID={confirmTestID}
            variant='solid'
          >
            {isLoading ? (
              <ButtonSpinner
                className='color-white'
                size='small'
              />
            ) : null}
            <ButtonText className='text-white data-[hover=true]:text-white data-[active=true]:text-white data-[disabled=true]:text-white'>
              {confirmLabel}
            </ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
